// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * HomePage - /home
 * Ontvangst- en oriëntatiepunt van WilsApp.
 *
 * Doel: rust uitstralen, één duidelijke vraag beantwoorden: "wat doe ik nu?"
 * Max 2 primaire acties zichtbaar. Geen modules, geen voortgangsoverzichten.
 *
 * Pilotvisie 50+/65+:
 * - Één hoofdactie bovenaan
 * - "Mijn dossiers" alleen zichtbaar als reallyDone (≥ 80% modules klaar)
 * - "Terug naar introductie" altijd zichtbaar
 * - Geen misleidende "afgerond"-tekst bij lege modules
 */
import { useNavigate, useLocation }  from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  Leaf, ArrowRight, Play, RotateCcw, LayoutDashboard,
  BookOpen, CheckCircle2, Home } from 'lucide-react'
import { useWizardStore, useAuthStore } from '@/store'
import { ROUTES, WIZARD_ROUTE, DOSSIER_OVERVIEW } from '@/lib/constants/routes'
import { } from '@/services/profile'
import { getFirstProfile } from '@/services/profile'

// Vaste deler - altijd 10, ongeacht hoeveel modules bezocht zijn

export function HomePage() {
  const navigate                                          = useNavigate()
  const { currentStep, isCompleted, totalSteps }         = useWizardStore()
  const { userId }                                        = useAuthStore()
  const [firstName,  setFirstName]                        = useState('')
  const [showSavedBanner, setShowSavedBanner]               = useState(false)

  useEffect(() => {
    if (!userId) return
    getFirstProfile().then(p => { if (p) setFirstName(p.firstName) })
  }, [userId])

  const location = useLocation()

  useEffect(() => {
    if ((location.state as { savedFromWizard?: boolean })?.savedFromWizard) {
      setShowSavedBanner(true)
      const t = setTimeout(() => setShowSavedBanner(false), 3000)
      return () => clearTimeout(t)
    }
  }, [location.state])

  const hour     = new Date().getHours()
  const greeting = hour < 12 ? 'Goedemorgen' : hour < 18 ? 'Goedemiddag' : 'Goedenavond'

  const reallyDone   = isCompleted

  // Wizard-navigatiestaten
  const isNew        = currentStep === 0 && !isCompleted
  const isInProgress = !isCompleted && currentStep > 0
  const isDoneNotReady = isCompleted && !reallyDone   // doorlopen maar modules onvoldoende


  return (
    <div className="flex flex-col gap-6 animate-fade-in">

      {/* ── Opgeslagen-banner na terugkeer uit wizard ─────────────────── */}
      {showSavedBanner && (
        <div className="flex items-center gap-2.5 rounded-xl bg-sage-50 border border-sage-200 px-4 py-3 animate-fade-in">
          <span className="text-base flex-shrink-0">✓</span>
          <p className="text-sm text-sage-800 font-medium">
            Uw gegevens zijn automatisch opgeslagen.
          </p>
        </div>
      )}

      {/* ── Hero - begroeting ──────────────────────────────────────────────── */}
      <div className="rounded-3xl bg-gradient-to-br from-moss to-sage-700 px-6 pt-8 pb-7 text-white shadow-card">
        <div className="flex items-center gap-3 mb-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
            <Leaf className="h-5 w-5 text-white" />
          </div>
          <span className="text-sm font-medium text-white/80">WilsApp</span>
        </div>
        <h1 className="text-2xl font-bold text-white leading-tight mb-2">
          {firstName ? `${greeting}, ${firstName}` : 'Welkom bij WilsApp'}
        </h1>
        <p className="text-sm text-white/75 leading-relaxed mb-5">
          WilsApp begeleidt u stap voor stap bij wat belangrijk is voor u en uw naasten.
        </p>

        {/* Statusregel - alleen tonen als er voortgang is, nooit misleidend */}
        
      </div>

      {/* ── Eerste actie - één per staat ────────────────────────────────── */}
      <div className="flex flex-col gap-3">

        {/* Staat 1: Nog niet begonnen */}
        {isNew && (
          <button
            onClick={() => navigate(WIZARD_ROUTE)}
            className="flex items-center gap-4 rounded-2xl bg-moss text-white px-5 py-4 shadow-card hover:bg-moss-light active:scale-[0.98] transition-all"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
              <Play className="h-5 w-5" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-base font-semibold">Voorbereiding starten</p>
              <p className="text-xs text-white/70 mt-0.5">Doorloop stap voor stap de begeleide wizard</p>
            </div>
            <ArrowRight className="h-4 w-4 text-white/60 flex-shrink-0" />
          </button>
        )}

        {/* Staat 2: Bezig in wizard */}
        {isInProgress && (
          <button
            onClick={() => navigate(WIZARD_ROUTE)}
            className="flex items-center gap-4 rounded-2xl bg-moss text-white px-5 py-4 shadow-card hover:bg-moss-light active:scale-[0.98] transition-all"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
              <RotateCcw className="h-5 w-5" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-base font-semibold">Verder waar ik gebleven was</p>
              <p className="text-xs text-white/70 mt-0.5">
                Stap {currentStep + 1} van {totalSteps}
              </p>
            </div>
            <ArrowRight className="h-4 w-4 text-white/60 flex-shrink-0" />
          </button>
        )}

        {/* Staat 3: Wizard doorlopen maar < 80% modules klaar */}
        {isDoneNotReady && (
          <button
            onClick={() => navigate(WIZARD_ROUTE)}
            className="flex items-center gap-4 rounded-2xl bg-moss text-white px-5 py-4 shadow-card hover:bg-moss-light active:scale-[0.98] transition-all"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
              <RotateCcw className="h-5 w-5" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-base font-semibold">Ga verder met uw voorbereiding</p>
              <p className="text-xs text-white/70 mt-0.5">
                Vervolg de onderdelen die nog aandacht nodig hebben.
              </p>
            </div>
            <ArrowRight className="h-4 w-4 text-white/60 flex-shrink-0" />
          </button>
        )}

        {/* Staat 4: Écht klaar (reallyDone) */}
        {reallyDone && (
          <button
            onClick={() => navigate(ROUTES.DASHBOARD)}
            className="flex items-center gap-4 rounded-2xl bg-moss text-white px-5 py-4 shadow-card hover:bg-moss-light active:scale-[0.98] transition-all"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-base font-semibold">Bekijk mijn overzicht</p>
              <p className="text-xs text-white/70 mt-0.5">Uw voorbereiding is afgerond</p>
            </div>
            <ArrowRight className="h-4 w-4 text-white/60 flex-shrink-0" />
          </button>
        )}

        {/* Dashboard - altijd zichtbaar als secundaire actie (tenzij reallyDone) */}
        {!reallyDone && (
          <button
            onClick={() => navigate(ROUTES.DASHBOARD)}
            className="flex items-center gap-4 rounded-2xl bg-white border border-sand-300 px-5 py-4 hover:bg-sand-50 active:scale-[0.98] transition-all"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sand-200 text-ink-3 flex-shrink-0">
              <LayoutDashboard className="h-5 w-5" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-ink">Dashboard openen</p>
              <p className="text-xs text-ink-4 mt-0.5">Overzicht van uw voortgang</p>
            </div>
            <ArrowRight className="h-4 w-4 text-ink-4 flex-shrink-0" />
          </button>
        )}

      </div>

      {/* ── Mijn dossiers - alleen zichtbaar als reallyDone ───────────────── */}
      {reallyDone && (
        <button
          onClick={() => navigate(DOSSIER_OVERVIEW)}
          className="flex items-center gap-4 rounded-2xl bg-white border border-sand-300 px-5 py-4 text-left hover:bg-sand-50 active:scale-[0.98] transition-all"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 flex-shrink-0">
            <BookOpen className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-ink">Mijn dossiers</p>
            <p className="text-xs text-ink-4 mt-0.5">Nabestaandendossier en notarisdossier</p>
          </div>
          <ArrowRight className="h-4 w-4 text-ink-4 flex-shrink-0" />
        </button>
      )}

      {/* ── Terug naar introductie - altijd zichtbaar ─────────────────────── */}
      <button
        onClick={() => navigate(WIZARD_ROUTE + '?welcome=1')}
        className="flex items-center gap-4 rounded-2xl bg-white border border-sand-300 px-5 py-4 text-left hover:bg-sand-50 active:scale-[0.98] transition-all"
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sage-50 flex-shrink-0">
          <Home className="h-5 w-5 text-sage-600" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-sm font-semibold text-ink">Terug naar introductie</p>
          <p className="text-xs text-ink-4 mt-0.5">Bekijk de uitleg over WilsApp opnieuw</p>
        </div>
        <ArrowRight className="h-4 w-4 text-ink-4 flex-shrink-0" />
      </button>

    </div>
  )
}
